/**
 * Pixel Tracking Utilities
 * This file contains functions to inject and initialize tracking pixels
 * Google Analytics, Facebook Pixel, and TikTok Pixel
 */

// Google Analytics
export const initGoogleAnalytics = (trackingId: string) => {
  if (!trackingId || typeof window === 'undefined') return;

  // Check if already loaded
  if (typeof window.gtag === 'function') {
    console.log('Google Analytics already loaded');
    return;
  }

  // Load Google Analytics script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', trackingId);

  console.log(`✅ Google Analytics initialized: ${trackingId}`);
};

// Facebook Pixel (supports multiple pixels)
export const initFacebookPixels = (pixelIds: string[]) => {
  if (!pixelIds || pixelIds.length === 0 || typeof window === 'undefined') return;

  pixelIds.forEach((pixelId) => {
    if (!pixelId || pixelId.trim() === '') return;

    // Check if Facebook Pixel is already initialized
    if (!window.fbq) {
      // Load Facebook Pixel script (only once)
      const script = document.createElement('script');
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
      `;
      document.head.appendChild(script);
    }

    // Initialize pixel
    if (window.fbq) {
      window.fbq('init', pixelId);
      window.fbq('track', 'PageView');
      console.log(`✅ Facebook Pixel initialized: ${pixelId}`);
    }
  });
};

// TikTok Pixel
export const initTikTokPixel = (pixelId: string) => {
  if (!pixelId || typeof window === 'undefined') return;

  // Check if already loaded
  if (window.ttq) {
    console.log('TikTok Pixel already loaded');
    return;
  }

  // Load TikTok Pixel script
  const script = document.createElement('script');
  script.innerHTML = `
    !function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
      ttq.load('${pixelId}');
      ttq.page();
    }(window, document, 'ttq');
  `;
  document.head.appendChild(script);

  console.log(`✅ TikTok Pixel initialized: ${pixelId}`);
};

// Initialize all pixels from page settings
export const initAllPixels = (analytics: {
  googleAnalyticsId?: string;
  facebookPixelIds: string[];
  tiktokPixelId?: string;
}) => {
  console.log('🚀 Initializing tracking pixels...');

  // Google Analytics
  if (analytics.googleAnalyticsId) {
    initGoogleAnalytics(analytics.googleAnalyticsId);
  }

  // Facebook Pixels
  if (analytics.facebookPixelIds && analytics.facebookPixelIds.length > 0) {
    initFacebookPixels(analytics.facebookPixelIds);
  }

  // TikTok Pixel
  if (analytics.tiktokPixelId) {
    initTikTokPixel(analytics.tiktokPixelId);
  }

  console.log('✅ All pixels initialized successfully!');
};

// Track custom events
export const trackEvent = (eventName: string, eventData?: any) => {
  if (typeof window === 'undefined') return;

  // Google Analytics Event
  if (window.gtag) {
    window.gtag('event', eventName, eventData);
    console.log(`📊 GA Event: ${eventName}`, eventData);
  }

  // Facebook Pixel Event
  if (window.fbq) {
    window.fbq('track', eventName, eventData);
    console.log(`📘 FB Event: ${eventName}`, eventData);
  }

  // TikTok Pixel Event
  if (window.ttq) {
    window.ttq.track(eventName, eventData);
    console.log(`🎵 TikTok Event: ${eventName}`, eventData);
  }
};

// TypeScript declarations for global objects
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    fbq: (...args: any[]) => void;
    _fbq: any;
    ttq: any;
    TiktokAnalyticsObject: string;
  }
}

export default {
  initGoogleAnalytics,
  initFacebookPixels,
  initTikTokPixel,
  initAllPixels,
  trackEvent,
};

